import { LOBBY_SCOPE } from './node.js';
import {
  parsePeerId,
  playerIdFromPeerId
} from '../security/input.js';
import { normalizeCountryCode } from './countries.js';
import { lobbyDebug } from './lobbyDebug.js';

const DEFAULT_PEERBASKET_BASE = 'https://peerbasket.bittu.dev/basket';

/** Compact identity advert: x-<16hexPlayerId>-<CC><utf8hexNickname> */
const IDENTITY_RE = /^x-([a-f0-9]{16})-([a-z]{2}|xx)([a-f0-9]*)$/i;
/** Legacy long advert (still parsed if present in a basket). */
const LEGACY_META_RE = /^dechess-meta:(dc-[a-f0-9]{16}-[a-f0-9]{10}):([A-Za-z0-9_-]+)$/i;
/**
 * Compact challenge advert (PeerBasket out-of-band inbox):
 * c-<target16>-<game32>-<challenger16><session10>-<rated0|1><timeControlHex>
 */
const CHALLENGE_RE = /^c-([a-f0-9]{16})-([a-f0-9]{32})-([a-f0-9]{16})([a-f0-9]{10})-([01])([a-f0-9]*)$/i;
/**
 * Compact lobby chat advert (PeerBasket — WebRTC often never opens for Online now peers):
 * t-<sender16><session10>-<sentAtHex>-<roomHex>-<textHex>
 */
const CHAT_RE = /^t-([a-f0-9]{16})([a-f0-9]{10})-([a-f0-9]+)-([a-f0-9]+)-([a-f0-9]+)$/i;
/**
 * Compact lobby room advert:
 * r-<creator16><session10>-<createdAtHex>-<roomHex>-<titleHex>
 */
const ROOM_RE = /^r-([a-f0-9]{16})([a-f0-9]{10})-([a-f0-9]+)-([a-f0-9]+)-([a-f0-9]+)$/i;

/**
 * PeerBasket / Cloudflare ~20 req/min per IP. Two browsers on one LAN share that
 * budget (Chrome + Brave = one IP), so keep a hard inter-request gap and
 * piggyback chat/challenge adverts on the inbox poll.
 *
 * Browser "CORS" errors here are usually Cloudflare 429/WAF HTML without ACAO —
 * Chrome often surfaces them first while Brave still has a few successful polls.
 */
export const LOBBY_RENDEZVOUS_MIN_INTERVAL_MS = 18_000;
export const LOBBY_RENDEZVOUS_RATE_LIMIT_BACKOFF_MS = 60_000;
/** Shorter backoff for primary inbox polls (self peer_id) so chat/challenges can land. */
export const LOBBY_INBOX_RATE_LIMIT_BACKOFF_MS = 30_000;
/** Challenges may post more often than soft discovery pacing — still avoid Cloudflare 429. */
export const LOBBY_CHALLENGE_MIN_INTERVAL_MS = 12_000;
export const LOBBY_CHAT_MIN_INTERVAL_MS = 8_000;
/**
 * Hard gap between ANY PeerBasket HTTP calls from this origin tab.
 * Two browsers × ~1 cycle/20s × 2 posts must stay under ~20/min/IP.
 */
export const LOBBY_BASKET_MIN_GAP_MS = 10_000;
export const LOBBY_CHAT_ADVERT_TTL_MS = 30 * 60 * 1000;
export const LOBBY_IDENTITY_ADVERT_EVERY = 5;
export const LOBBY_CHALLENGE_ADVERT_TTL_MS = 15 * 60 * 1000;
/** Cross-tab shared backoff cursor (same browser origin). */
const BASKET_BACKOFF_STORAGE_KEY = 'dechess-peerbasket-backoff-until';

const basketState = new Map();
/** Shared across all peer sessions on this origin (Cloudflare rate-limits by IP). */
const globalGate = {
  nextAllowedAt: 0,
  /** Earliest wall-clock for the next PeerBasket HTTP call. */
  nextRequestAt: 0,
  /** Hard block for secondary advert POSTs after HTTP 429 / CORS-as-WAF. */
  advertBlockedUntil: 0,
  /** Hard block for primary inbox polls after HTTP 429 (shorter). */
  inboxBlockedUntil: 0,
  /** @deprecated alias kept for tests/reset — maps to advertBlockedUntil */
  get challengeBlockedUntil() {
    return this.advertBlockedUntil;
  },
  set challengeBlockedUntil(value) {
    this.advertBlockedUntil = value;
  },
  /** Soft pace between successful challenge posts. */
  challengeNextAllowedAt: 0,
  /** Soft pace between successful chat posts. */
  chatNextAllowedAt: 0,
  identityCycle: 0,
  /** Serialize PeerBasket HTTP so force-publish cannot race the inbox poll. */
  requestChain: Promise.resolve()
};

function unionPeerLists(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    for (const value of list || []) {
      const key = typeof value === 'string'
        ? value
        : (value?.peer_id || value?.peerId || JSON.stringify(value));
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      out.push(value);
    }
  }
  return out;
}

/** Compact challenge advert kept for republish on every rendezvous cycle. */
let queuedChallengeAdvert = null;
/** Compact chat advert kept for republish while WebRTC is unavailable. */
let queuedChatAdvert = null;
/** Compact room advert kept for republish while WebRTC is unavailable. */
let queuedRoomAdvert = null;

/** Keep (re)posting this challenge advert until cleared — Online now inbox path. */
export function queueLobbyChallengeAdvert(challenge) {
  queuedChallengeAdvert = challenge ? encodeLobbyChallengeAdvert(challenge) : null;
  return queuedChallengeAdvert;
}

export function clearQueuedLobbyChallengeAdvert() {
  queuedChallengeAdvert = null;
}

export function peekQueuedLobbyChallengeAdvert() {
  return queuedChallengeAdvert;
}

export function queueLobbyChatAdvert(payload, peerId) {
  queuedChatAdvert = payload && peerId
    ? encodeLobbyChatAdvert(payload, peerId)
    : null;
  return queuedChatAdvert;
}

export function clearQueuedLobbyChatAdvert() {
  queuedChatAdvert = null;
}

export function peekQueuedLobbyChatAdvert() {
  return queuedChatAdvert;
}

export function queueLobbyRoomAdvert(payload, peerId) {
  queuedRoomAdvert = payload && peerId
    ? encodeLobbyRoomAdvert(payload, peerId)
    : null;
  return queuedRoomAdvert;
}

export function clearQueuedLobbyRoomAdvert() {
  queuedRoomAdvert = null;
}

export function peekQueuedLobbyRoomAdvert() {
  return queuedRoomAdvert;
}

function configuredRendezvousBase() {
  const fromEnv = (import.meta.env?.VITE_LOBBY_RENDEZVOUS_URL || '').trim();
  if (fromEnv === 'off' || fromEnv === 'disabled') {
    return '';
  }
  if ((import.meta.env?.VITE_E2E_RELAY_URL || '').trim()) {
    return '';
  }
  if (import.meta.env?.MODE === 'test' || import.meta.env?.VITEST) {
    return '';
  }
  if (fromEnv === 'on' || fromEnv === 'default' || fromEnv === 'peerbasket') {
    return DEFAULT_PEERBASKET_BASE;
  }
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }
  // Required for Online now cold discovery (names/flags) across browsers.
  return DEFAULT_PEERBASKET_BASE;
}

function fromBase64Url(value) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  return decodeURIComponent(escape(atob(padded + pad)));
}

function utf8ToHex(value) {
  return [...new TextEncoder().encode(String(value || ''))]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function hexToUtf8(value) {
  const hex = String(value || '');
  if (!hex || hex.length % 2 !== 0 || /[^a-f0-9]/i.test(hex)) {
    return '';
  }
  try {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return new TextDecoder().decode(bytes).trim().slice(0, 32);
  } catch {
    return '';
  }
}

export function lobbyRendezvousBasketId(scope = LOBBY_SCOPE) {
  return `dechess.org:${scope}`;
}

/**
 * Compact PeerBasket-safe identity advert (alphanumeric + dashes only).
 * Matched to live peers by playerId prefix once both appear in the basket.
 */
export function encodeLobbyRendezvousIdentity(peerId, {
  nickname = '',
  country = '',
  playerId = ''
} = {}) {
  const parsed = parsePeerId(peerId);
  const resolvedPlayerId = (playerId || playerIdFromPeerId(parsed) || '').toLowerCase();
  const match = resolvedPlayerId.match(/^dc-([a-f0-9]{16})$/);
  if (!match) {
    return null;
  }
  const cc = (normalizeCountryCode(country) || 'XX').toLowerCase();
  const nickHex = utf8ToHex(String(nickname || '').trim().slice(0, 24));
  return `x-${match[1]}-${cc}${nickHex}`;
}

export function parseLobbyRendezvousIdentity(value) {
  const raw = String(value || '');
  const compact = raw.match(IDENTITY_RE);
  if (compact) {
    const playerId = `dc-${compact[1].toLowerCase()}`;
    const country = normalizeCountryCode(compact[2]) || '';
    return {
      playerId,
      peerId: null,
      nickname: hexToUtf8(compact[3]),
      country,
      openToChallenges: true,
      lastSeenAt: Date.now()
    };
  }

  const legacy = raw.match(LEGACY_META_RE);
  if (!legacy) {
    return null;
  }
  const peerId = legacy[1].toLowerCase();
  try {
    const data = JSON.parse(fromBase64Url(legacy[2]));
    return {
      peerId,
      playerId: playerIdFromPeerId(peerId),
      nickname: String(data.n || '').trim().slice(0, 32),
      country: normalizeCountryCode(data.c) || '',
      openToChallenges: data.o !== 0 && data.o !== false,
      lastSeenAt: Date.now()
    };
  } catch {
    return null;
  }
}

function uuidFromCompactHex(value) {
  const hex = String(value || '').toLowerCase();
  if (!/^[a-f0-9]{32}$/.test(hex)) {
    return null;
  }
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20)
  ].join('-');
}

export function encodeLobbyChallengeAdvert(challenge) {
  const targetMatch = String(challenge?.targetPlayerId || '')
    .toLowerCase()
    .match(/^dc-([a-f0-9]{16})$/);
  const peer = parsePeerId(challenge?.challenger?.peerId);
  const peerMatch = peer?.match(/^dc-([a-f0-9]{16})-([a-f0-9]{10})$/);
  const gameHex = String(challenge?.gameId || '').replace(/-/g, '').toLowerCase();
  if (!targetMatch || !peerMatch || !/^[a-f0-9]{32}$/.test(gameHex)) {
    return null;
  }
  const rated = challenge.rated ? '1' : '0';
  const tcHex = utf8ToHex(String(challenge.timeControl || '3+0').slice(0, 12));
  return `c-${targetMatch[1]}-${gameHex}-${peerMatch[1]}${peerMatch[2]}-${rated}${tcHex}`;
}

export function parseLobbyChallengeAdvert(value, {
  selfPlayerId = '',
  now = Date.now()
} = {}) {
  const match = String(value || '').match(CHALLENGE_RE);
  if (!match) {
    return null;
  }
  const targetPlayerId = `dc-${match[1].toLowerCase()}`;
  if (
    selfPlayerId
    && String(selfPlayerId).toLowerCase() !== targetPlayerId
  ) {
    return null;
  }
  const gameId = uuidFromCompactHex(match[2]);
  if (!gameId) {
    return null;
  }
  const challengerPlayerId = `dc-${match[3].toLowerCase()}`;
  const challengerPeerId = `dc-${match[3].toLowerCase()}-${match[4].toLowerCase()}`;
  const timeControl = hexToUtf8(match[6]) || '3+0';
  return {
    challengeId: `basket-${gameId}`,
    gameId,
    targetPlayerId,
    challenger: {
      playerId: challengerPlayerId,
      peerId: challengerPeerId,
      nickname: '',
      country: ''
    },
    timeControl,
    variant: 'standard',
    rated: match[5] === '1',
    sentAt: now,
    expiresAt: now + LOBBY_CHALLENGE_ADVERT_TTL_MS,
    via: 'peerbasket'
  };
}

export function encodeLobbyChatAdvert(payload, peerId) {
  const peer = parsePeerId(peerId);
  const peerMatch = peer?.match(/^dc-([a-f0-9]{16})-([a-f0-9]{10})$/);
  const room = String(payload?.roomId || 'main')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '')
    .slice(0, 32) || 'main';
  const text = String(payload?.text || '').trim().slice(0, 80);
  const sentAt = Number(payload?.sentAt) || Date.now();
  if (!peerMatch || !text) {
    return null;
  }
  const roomHex = utf8ToHex(room);
  const textHex = utf8ToHex(text);
  const tsHex = Math.max(0, Math.floor(sentAt)).toString(16);
  return `t-${peerMatch[1]}${peerMatch[2]}-${tsHex}-${roomHex}-${textHex}`;
}

export function parseLobbyChatAdvert(value, {
  selfPeerId = '',
  now = Date.now()
} = {}) {
  const match = String(value || '').match(CHAT_RE);
  if (!match) {
    return null;
  }
  const senderPeerId = `dc-${match[1].toLowerCase()}-${match[2].toLowerCase()}`;
  if (selfPeerId && parsePeerId(selfPeerId) === senderPeerId) {
    return null;
  }
  const sentAt = Number.parseInt(match[3], 16);
  if (!Number.isFinite(sentAt) || now - sentAt > LOBBY_CHAT_ADVERT_TTL_MS || sentAt > now + 60_000) {
    return null;
  }
  const roomId = hexToUtf8(match[4]) || 'main';
  const text = hexToUtf8(match[5]);
  if (!text) {
    return null;
  }
  return {
    roomId,
    text,
    nickname: '',
    playerId: `dc-${match[1].toLowerCase()}`,
    sentAt,
    senderId: senderPeerId,
    via: 'peerbasket',
    messageKey: `${senderPeerId}:${sentAt}:${text.slice(0, 24)}`
  };
}

export function encodeLobbyRoomAdvert(payload, peerId) {
  const peer = parsePeerId(peerId);
  const peerMatch = peer?.match(/^dc-([a-f0-9]{16})-([a-f0-9]{10})$/);
  const room = String(payload?.roomId || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '')
    .slice(0, 32);
  const title = String(payload?.title || room).trim().slice(0, 32);
  const createdAt = Number(payload?.createdAt) || Date.now();
  if (!peerMatch || !room || room === 'main') {
    return null;
  }
  return `r-${peerMatch[1]}${peerMatch[2]}-${Math.max(0, Math.floor(createdAt)).toString(16)}-${utf8ToHex(room)}-${utf8ToHex(title)}`;
}

export function parseLobbyRoomAdvert(value, {
  selfPeerId = '',
  now = Date.now()
} = {}) {
  const match = String(value || '').match(ROOM_RE);
  if (!match) {
    return null;
  }
  const creatorPeerId = `dc-${match[1].toLowerCase()}-${match[2].toLowerCase()}`;
  if (selfPeerId && parsePeerId(selfPeerId) === creatorPeerId) {
    return null;
  }
  const createdAt = Number.parseInt(match[3], 16);
  if (!Number.isFinite(createdAt) || now - createdAt > LOBBY_CHAT_ADVERT_TTL_MS) {
    return null;
  }
  const roomId = hexToUtf8(match[4]);
  const title = hexToUtf8(match[5]) || roomId;
  if (!roomId || roomId === 'main') {
    return null;
  }
  return {
    roomId,
    title,
    createdBy: `dc-${match[1].toLowerCase()}`,
    createdAt,
    senderId: creatorPeerId,
    via: 'peerbasket'
  };
}

export function parseRendezvousPeerList(rawPeers = [], selfPeerId = '') {
  const selfId = parsePeerId(selfPeerId);
  const selfPlayerId = playerIdFromPeerId(selfId);
  const byPeerId = new Map();
  const identityByPlayerId = new Map();
  const challenges = [];
  const chats = [];
  const rooms = [];

  for (const value of rawPeers) {
    const raw = typeof value === 'string'
      ? value
      : (value?.peer_id || value?.peerId || '');
    const challenge = parseLobbyChallengeAdvert(raw, { selfPlayerId });
    if (challenge) {
      challenges.push(challenge);
      continue;
    }
    const chat = parseLobbyChatAdvert(raw, { selfPeerId: selfId });
    if (chat) {
      chats.push(chat);
      continue;
    }
    const room = parseLobbyRoomAdvert(raw, { selfPeerId: selfId });
    if (room) {
      rooms.push(room);
      continue;
    }
    const identity = parseLobbyRendezvousIdentity(raw);
    if (identity?.playerId) {
      if (selfPlayerId && identity.playerId === selfPlayerId) {
        continue;
      }
      const previous = identityByPlayerId.get(identity.playerId) || {};
      identityByPlayerId.set(identity.playerId, {
        ...previous,
        ...identity,
        nickname: identity.nickname || previous.nickname || '',
        country: identity.country || previous.country || ''
      });
      continue;
    }

    const peerId = parsePeerId(raw);
    if (!peerId || (selfId && peerId === selfId)) {
      continue;
    }
    if (!byPeerId.has(peerId)) {
      byPeerId.set(peerId, {
        peerId,
        playerId: playerIdFromPeerId(peerId),
        lastSeenAt: Date.now()
      });
    }
  }

  for (const [peerId, entry] of byPeerId.entries()) {
    const playerId = entry.playerId || playerIdFromPeerId(peerId);
    const identity = playerId ? identityByPlayerId.get(playerId) : null;
    byPeerId.set(peerId, {
      ...entry,
      playerId,
      nickname: identity?.nickname || entry.nickname || '',
      country: identity?.country || entry.country || '',
      openToChallenges: identity
        ? identity.openToChallenges !== false
        : entry.openToChallenges !== false,
      lastSeenAt: Math.max(entry.lastSeenAt || 0, identity?.lastSeenAt || 0)
    });
  }

  return {
    peers: [...byPeerId.values()],
    challenges,
    chats,
    rooms
  };
}

function getBasketState(key) {
  if (!basketState.has(key)) {
    basketState.set(key, {
      nextAllowedAt: 0,
      inflight: null,
      lastPeers: [],
      lastResult: { peers: [], challenges: [], chats: [], rooms: [] }
    });
  }
  return basketState.get(key);
}

/** Test helper: clear client-side rate-limit / inflight state. */
export function resetLobbyRendezvousState() {
  basketState.clear();
  globalGate.nextAllowedAt = 0;
  globalGate.nextRequestAt = 0;
  globalGate.advertBlockedUntil = 0;
  globalGate.inboxBlockedUntil = 0;
  globalGate.challengeNextAllowedAt = 0;
  globalGate.chatNextAllowedAt = 0;
  globalGate.identityCycle = 0;
  globalGate.requestChain = Promise.resolve();
  queuedChallengeAdvert = null;
  queuedChatAdvert = null;
  queuedRoomAdvert = null;
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(BASKET_BACKOFF_STORAGE_KEY);
    } catch {
      // Ignore.
    }
  }
}

export function isBasketBudgetAvailable(now = Date.now(), minGapMs = LOBBY_BASKET_MIN_GAP_MS) {
  syncBasketBackoffFromStorage(now);
  return now >= globalGate.nextRequestAt && minGapMs >= 0;
}

function reserveBasketBudget(now = Date.now(), minGapMs = LOBBY_BASKET_MIN_GAP_MS) {
  globalGate.nextRequestAt = now + Math.max(0, minGapMs);
}

function readStoredBasketBackoff() {
  if (typeof localStorage === 'undefined') {
    return 0;
  }
  try {
    const raw = Number(localStorage.getItem(BASKET_BACKOFF_STORAGE_KEY) || 0);
    return Number.isFinite(raw) ? raw : 0;
  } catch {
    return 0;
  }
}

function writeStoredBasketBackoff(until) {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(BASKET_BACKOFF_STORAGE_KEY, String(Math.max(0, until)));
  } catch {
    // Ignore quota / private mode.
  }
}

function syncBasketBackoffFromStorage(now = Date.now()) {
  const stored = readStoredBasketBackoff();
  if (stored > now) {
    globalGate.advertBlockedUntil = Math.max(globalGate.advertBlockedUntil, stored);
    globalGate.inboxBlockedUntil = Math.max(globalGate.inboxBlockedUntil, stored);
    globalGate.nextAllowedAt = Math.max(globalGate.nextAllowedAt, stored);
    globalGate.nextRequestAt = Math.max(globalGate.nextRequestAt, stored);
  }
}

function classifyBasketFailure(error, response = null) {
  const status = Number(response?.status || error?.status) || 0;
  // Opaque browser CORS failures (often CF 429/WAF HTML without ACAO).
  if (status === 0) {
    return { status: 0, kind: 'cors-or-waf' };
  }
  if (status === 429) {
    return { status: 429, kind: 'rate-limited' };
  }
  return { status, kind: status ? `http-${status}` : 'network' };
}

function armBasketFailureBackoff(now, classification, {
  rateLimitBackoffMs = LOBBY_RENDEZVOUS_RATE_LIMIT_BACKOFF_MS,
  inboxBackoffMs = LOBBY_INBOX_RATE_LIMIT_BACKOFF_MS,
  blockInbox = false
} = {}) {
  if (classification.kind !== 'cors-or-waf' && classification.status !== 429) {
    return;
  }
  // CORS-as-WAF needs a longer cool-down — Chrome often trips first on a shared IP.
  const backoffMs = classification.kind === 'cors-or-waf'
    ? Math.max(rateLimitBackoffMs, 60_000)
    : rateLimitBackoffMs;
  const until = now + backoffMs;
  globalGate.advertBlockedUntil = Math.max(globalGate.advertBlockedUntil, until);
  globalGate.nextAllowedAt = Math.max(globalGate.nextAllowedAt, until);
  globalGate.nextRequestAt = Math.max(globalGate.nextRequestAt, until);
  if (blockInbox || classification.kind === 'cors-or-waf') {
    globalGate.inboxBlockedUntil = Math.max(
      globalGate.inboxBlockedUntil,
      now + Math.max(inboxBackoffMs, backoffMs)
    );
  }
  writeStoredBasketBackoff(until);
  lobbyDebug('basket-backoff-armed', {
    kind: classification.kind,
    status: classification.status,
    backoffMs,
    until: new Date(until).toISOString(),
    hint: classification.kind === 'cors-or-waf'
      ? 'Not a real CORS misconfig — Cloudflare WAF/429 HTML lacks ACAO. Shared IP (Chrome+Brave) often trips one browser first.'
      : 'PeerBasket rate limit — pause polls.'
  });
}

async function postBasketPeer(baseUrl, scope, peerId, fetchImpl, {
  now = Date.now(),
  minGapMs = LOBBY_BASKET_MIN_GAP_MS,
  /** When false, skip gap check/reserve (same-cycle secondary after primary). */
  reserveBudget = true
} = {}) {
  const run = async () => {
    if (reserveBudget) {
      if (!isBasketBudgetAvailable(now, minGapMs)) {
        const error = new Error('PeerBasket budget exhausted');
        error.status = 0;
        error.kind = 'budget';
        throw error;
      }
      reserveBasketBudget(now, minGapMs);
    }
    const basket = encodeURIComponent(lobbyRendezvousBasketId(scope));
    let response;
    try {
      response = await fetchImpl(`${baseUrl}/${basket}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ peer_id: peerId }),
        mode: 'cors',
        signal: typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
          ? AbortSignal.timeout(8000)
          : undefined
      });
    } catch (networkError) {
      const classified = classifyBasketFailure(networkError);
      const error = new Error('Lobby rendezvous blocked (cors-or-waf)');
      error.status = classified.status;
      error.kind = classified.kind;
      throw error;
    }
    if (!response.ok) {
      const classified = classifyBasketFailure(null, response);
      const error = new Error(`Lobby rendezvous failed (${classified.status})`);
      error.status = classified.status;
      error.kind = classified.kind;
      throw error;
    }
    return response.json();
  };

  const queued = globalGate.requestChain.then(run, run);
  globalGate.requestChain = queued.catch(() => undefined);
  return queued;
}

/**
 * Register this peer (+ occasional identity advert); return other enriched peers
 * and any challenge adverts addressed to this player.
 */
export async function fetchLobbyRendezvousPeers(selfPeerId, {
  scope = LOBBY_SCOPE,
  fetchImpl = globalThis.fetch,
  baseUrl = configuredRendezvousBase(),
  now = Date.now(),
  minIntervalMs = LOBBY_RENDEZVOUS_MIN_INTERVAL_MS,
  rateLimitBackoffMs = LOBBY_RENDEZVOUS_RATE_LIMIT_BACKOFF_MS,
  inboxBackoffMs = LOBBY_INBOX_RATE_LIMIT_BACKOFF_MS,
  minGapMs = LOBBY_BASKET_MIN_GAP_MS,
  nickname = '',
  country = '',
  playerId = '',
  force = false,
  includeIdentity = null
} = {}) {
  const empty = { peers: [], challenges: [], chats: [], rooms: [] };
  const selfId = parsePeerId(selfPeerId);
  if (!selfId || !baseUrl || typeof fetchImpl !== 'function') {
    return empty;
  }

  const stateKey = `${baseUrl}|${scope}|${selfId}`;
  const state = getBasketState(stateKey);
  if (!state.lastResult) {
    state.lastResult = empty;
    state.lastPeers = [];
  }

  // force must not share an in-flight soft poll — that response predates new adverts.
  if (state.inflight) {
    if (!force) {
      return state.inflight;
    }
    try {
      await state.inflight;
    } catch {
      // Continue with a fresh force poll.
    }
  }

  // Primary inbox poll. cors-or-waf cool-down blocks even force — shared IP
  // (Chrome+Brave) worsens if we keep hammering PeerBasket.
  syncBasketBackoffFromStorage(now);
  if (now < globalGate.inboxBlockedUntil) {
    lobbyDebug('basket-fetch-skip', {
      reason: readStoredBasketBackoff() > now ? 'cors-waf-cooldown' : 'inbox-backoff',
      selfId,
      force,
      until: new Date(globalGate.inboxBlockedUntil).toISOString()
    });
    return state.lastResult;
  }
  if (!force && now < globalGate.nextAllowedAt) {
    lobbyDebug('basket-fetch-skip', { reason: 'soft-pace', selfId });
    return state.lastResult;
  }
  if (!force && now < state.nextAllowedAt) {
    lobbyDebug('basket-fetch-skip', { reason: 'state-pace', selfId });
    return state.lastResult;
  }
  if (!isBasketBudgetAvailable(now, minGapMs)) {
    if (!force) {
      lobbyDebug('basket-fetch-skip', { reason: 'budget', selfId, force });
      return state.lastResult;
    }
    // Force inbox poll may proceed under soft budget pressure; secondary still
    // respects advertBlockedUntil. cors-waf cool-down already returned above.
    lobbyDebug('basket-fetch-force-over-budget', { selfId });
  }

  state.inflight = (async () => {
    try {
      // One budget token covers primary inbox + one secondary advert (chat/challenge).
      // Previously secondary always hit basket-secondary-skip:budget after primary.
      reserveBasketBudget(now, minGapMs);
      const primary = await postBasketPeer(baseUrl, scope, selfId, fetchImpl, {
        now,
        minGapMs,
        reserveBudget: false
      });
      let rawPeers = Array.isArray(primary?.peers)
        ? primary.peers
        : (Array.isArray(primary) ? primary : []);

      globalGate.identityCycle += 1;
      const canPostSecondary = now >= globalGate.advertBlockedUntil;
      // Fair queue: rotate so chat is not starved by a stuck challenge.
      // One secondary per cycle — chat/challenge delivery when WebRTC linked=0.
      const secondaryQueue = canPostSecondary
        ? (
          globalGate.identityCycle % 2 === 0
            ? [queuedChatAdvert, queuedChallengeAdvert, queuedRoomAdvert]
            : [queuedChallengeAdvert, queuedChatAdvert, queuedRoomAdvert]
        ).filter(Boolean)
        : [];
      const shouldPostIdentity = secondaryQueue.length === 0 && canPostSecondary && (
        includeIdentity === true
        || (includeIdentity !== false
          && globalGate.identityCycle % LOBBY_IDENTITY_ADVERT_EVERY === 0)
      );
      if (shouldPostIdentity) {
        const identityAdvert = encodeLobbyRendezvousIdentity(selfId, {
          nickname,
          country,
          playerId
        });
        if (identityAdvert) {
          secondaryQueue.push(identityAdvert);
        }
      }
      const secondaryAdverts = secondaryQueue.slice(0, 1);
      for (const secondaryAdvert of secondaryAdverts) {
        try {
          const secondary = await postBasketPeer(
            baseUrl,
            scope,
            secondaryAdvert,
            fetchImpl,
            { now, minGapMs, reserveBudget: false }
          );
          const secondaryPeers = Array.isArray(secondary?.peers)
            ? secondary.peers
            : (Array.isArray(secondary) ? secondary : []);
          rawPeers = unionPeerLists(rawPeers, secondaryPeers);
          if (secondaryAdvert === queuedChallengeAdvert) {
            globalGate.challengeNextAllowedAt = now + LOBBY_CHALLENGE_MIN_INTERVAL_MS;
            lobbyDebug('basket-secondary-ok', { kind: 'challenge', selfId });
          } else if (
            secondaryAdvert === queuedChatAdvert
            || secondaryAdvert === queuedRoomAdvert
          ) {
            globalGate.chatNextAllowedAt = now + LOBBY_CHAT_MIN_INTERVAL_MS;
            lobbyDebug('basket-secondary-ok', {
              kind: secondaryAdvert === queuedChatAdvert ? 'chat' : 'room',
              selfId
            });
          } else {
            lobbyDebug('basket-secondary-ok', { kind: 'identity', selfId });
          }
        } catch (secondaryError) {
          const classified = classifyBasketFailure(secondaryError);
          armBasketFailureBackoff(now, classified, {
            rateLimitBackoffMs,
            inboxBackoffMs,
            blockInbox: false
          });
          lobbyDebug('basket-secondary-fail', {
            status: classified.status,
            kind: classified.kind,
            selfId
          });
          break;
        }
      }

      const parsed = parseRendezvousPeerList(rawPeers, selfId);
      state.lastPeers = parsed.peers;
      state.lastResult = parsed;
      state.nextAllowedAt = now + minIntervalMs;
      globalGate.nextAllowedAt = now + minIntervalMs;
      globalGate.inboxBlockedUntil = Math.min(globalGate.inboxBlockedUntil, now);
      lobbyDebug('basket-fetch-ok', {
        selfId,
        force,
        peers: parsed.peers.length,
        chats: parsed.chats.length,
        challenges: parsed.challenges.length,
        rooms: parsed.rooms.length,
        peerIds: parsed.peers.map((peer) => peer.peerId)
      });
      return parsed;
    } catch (error) {
      const classified = classifyBasketFailure(error);
      const backoff = classified.kind === 'cors-or-waf' || classified.status === 429
        ? inboxBackoffMs
        : (classified.kind === 'budget' ? minIntervalMs : minIntervalMs);
      state.nextAllowedAt = now + backoff;
      globalGate.nextAllowedAt = now + backoff;
      if (classified.kind === 'cors-or-waf' || classified.status === 429) {
        armBasketFailureBackoff(now, classified, {
          rateLimitBackoffMs,
          inboxBackoffMs,
          blockInbox: true
        });
      }
      lobbyDebug('basket-fetch-fail', {
        selfId,
        force,
        status: classified.status,
        kind: classified.kind,
        backoff
      });
      return state.lastResult;
    } finally {
      state.inflight = null;
    }
  })();

  return state.inflight;
}

/**
 * PeerBasket out-of-band challenge inbox. Soft discovery pacing must not block
 * this — Online now often has no WebRTC link, so the advert is the only path.
 */
export async function publishLobbyChallengeAdvert(challenge, {
  scope = LOBBY_SCOPE,
  fetchImpl = globalThis.fetch,
  baseUrl = configuredRendezvousBase(),
  now = Date.now(),
  force = false,
  minGapMs = LOBBY_BASKET_MIN_GAP_MS
} = {}) {
  const advert = encodeLobbyChallengeAdvert(challenge);
  if (advert) {
    queuedChallengeAdvert = advert;
  }
  if (!advert || !baseUrl || typeof fetchImpl !== 'function') {
    return { published: false, queued: Boolean(advert), advert: null };
  }
  // force bypasses soft pacing only — never hard CF/WAF backoff or HTTP budget.
  if (now < globalGate.advertBlockedUntil) {
    lobbyDebug('challenge-publish-skip', { reason: 'advert-backoff', force });
    return { published: false, queued: true, advert, reason: 'rate-limited' };
  }
  if (!force && now < globalGate.challengeNextAllowedAt) {
    lobbyDebug('challenge-publish-skip', { reason: 'challenge-paced' });
    return { published: false, queued: true, advert, reason: 'challenge-paced' };
  }
  if (!isBasketBudgetAvailable(now, minGapMs)) {
    const waitMs = Math.max(0, globalGate.nextRequestAt - now);
    if (force && waitMs > 0 && waitMs <= Math.max(minGapMs, 10_000)) {
      await new Promise((resolve) => {
        setTimeout(resolve, waitMs + 25);
      });
      now = Date.now();
    }
  }
  if (!isBasketBudgetAvailable(now, minGapMs)) {
    lobbyDebug('challenge-publish-skip', { reason: 'budget', force });
    return { published: false, queued: true, advert, reason: 'budget' };
  }
  try {
    await postBasketPeer(baseUrl, scope, advert, fetchImpl, { now, minGapMs });
    globalGate.challengeNextAllowedAt = now + LOBBY_CHALLENGE_MIN_INTERVAL_MS;
    globalGate.nextAllowedAt = Math.max(
      globalGate.nextAllowedAt,
      now + Math.min(LOBBY_CHALLENGE_MIN_INTERVAL_MS, 3_000)
    );
    lobbyDebug('challenge-publish-ok', { advert: advert.slice(0, 48) });
    return { published: true, queued: true, advert };
  } catch (error) {
    const classified = classifyBasketFailure(error);
    armBasketFailureBackoff(now, classified, { blockInbox: false });
    lobbyDebug('challenge-publish-fail', {
      status: classified.status,
      kind: classified.kind
    });
    return {
      published: false,
      queued: true,
      advert,
      reason: classified.kind
    };
  }
}

/**
 * PeerBasket out-of-band lobby chat. Same constraint as challenges: Online now
 * peers are often visible without an open PeerJS data channel.
 */
export async function publishLobbyChatAdvert(payload, peerId, {
  scope = LOBBY_SCOPE,
  fetchImpl = globalThis.fetch,
  baseUrl = configuredRendezvousBase(),
  now = Date.now(),
  force = false,
  minGapMs = LOBBY_BASKET_MIN_GAP_MS
} = {}) {
  const advert = encodeLobbyChatAdvert(payload, peerId);
  if (advert) {
    queuedChatAdvert = advert;
  }
  if (!advert || !baseUrl || typeof fetchImpl !== 'function') {
    return { published: false, queued: Boolean(advert), advert: advert || null };
  }
  // Prefer queue + next inbox piggyback. Immediate POST only when budget allows.
  if (now < globalGate.advertBlockedUntil) {
    lobbyDebug('chat-publish-skip', {
      reason: 'advert-backoff',
      text: payload?.text,
      force
    });
    return { published: false, queued: true, advert, reason: 'rate-limited' };
  }
  if (!force && now < globalGate.chatNextAllowedAt) {
    lobbyDebug('chat-publish-skip', { reason: 'chat-paced', text: payload?.text });
    return { published: false, queued: true, advert, reason: 'chat-paced' };
  }
  if (!isBasketBudgetAvailable(now, minGapMs)) {
    lobbyDebug('chat-publish-skip', {
      reason: 'budget',
      text: payload?.text,
      force
    });
    return { published: false, queued: true, advert, reason: 'budget' };
  }
  try {
    await postBasketPeer(baseUrl, scope, advert, fetchImpl, { now, minGapMs });
    globalGate.chatNextAllowedAt = now + LOBBY_CHAT_MIN_INTERVAL_MS;
    globalGate.nextAllowedAt = Math.max(
      globalGate.nextAllowedAt,
      now + Math.min(LOBBY_CHAT_MIN_INTERVAL_MS, 2_000)
    );
    lobbyDebug('chat-publish-ok', {
      text: payload?.text,
      advert: advert.slice(0, 64)
    });
    return { published: true, queued: true, advert };
  } catch (error) {
    const classified = classifyBasketFailure(error);
    armBasketFailureBackoff(now, classified, { blockInbox: false });
    lobbyDebug('chat-publish-fail', {
      status: classified.status,
      kind: classified.kind,
      text: payload?.text
    });
    return {
      published: false,
      queued: true,
      advert,
      reason: classified.kind
    };
  }
}

export async function publishLobbyRoomAdvert(payload, peerId, {
  scope = LOBBY_SCOPE,
  fetchImpl = globalThis.fetch,
  baseUrl = configuredRendezvousBase(),
  now = Date.now(),
  force = false,
  minGapMs = LOBBY_BASKET_MIN_GAP_MS
} = {}) {
  const advert = encodeLobbyRoomAdvert(payload, peerId);
  if (advert) {
    queuedRoomAdvert = advert;
  }
  if (!advert || !baseUrl || typeof fetchImpl !== 'function') {
    return { published: false, queued: Boolean(advert), advert: advert || null };
  }
  if (now < globalGate.advertBlockedUntil) {
    return { published: false, queued: true, advert, reason: 'rate-limited' };
  }
  if (!force && now < globalGate.chatNextAllowedAt) {
    return { published: false, queued: true, advert, reason: 'chat-paced' };
  }
  if (!isBasketBudgetAvailable(now, minGapMs)) {
    return { published: false, queued: true, advert, reason: 'budget' };
  }
  try {
    await postBasketPeer(baseUrl, scope, advert, fetchImpl, { now, minGapMs });
    globalGate.chatNextAllowedAt = now + LOBBY_CHAT_MIN_INTERVAL_MS;
    globalGate.nextAllowedAt = Math.max(
      globalGate.nextAllowedAt,
      now + Math.min(LOBBY_CHAT_MIN_INTERVAL_MS, 2_000)
    );
    return { published: true, queued: true, advert };
  } catch (error) {
    const classified = classifyBasketFailure(error);
    armBasketFailureBackoff(now, classified, { blockInbox: false });
    return {
      published: false,
      queued: true,
      advert,
      reason: classified.kind
    };
  }
}
